/**
 * CustomerLocaleProvider — fetches the customer's locale preference from
 * /api/customer/profile (when signed in) + persists changes back via PATCH.
 *
 * Why a wrapper around LocaleProvider rather than inlining it:
 *   - The /api/customer/profile call requires an authenticated session.
 *     The unauthenticated landing page + sign-in flow still need the
 *     LocaleProvider to render strings, so we mount the provider with a
 *     synchronous default (resolveLocale(navigator.language)) on first
 *     render and upgrade it once the profile fetch resolves.
 *   - The persistence callback (PATCH on every setLocale) belongs at this
 *     layer, not inside the pure LocaleProvider primitive.
 *
 * Locale resolution order (per spec):
 *   1. customer_users.locale (from /api/customer/profile.locale)
 *   2. accounts.locale       (from /api/customer/profile.accountLocale)
 *   3. navigator.language
 *   4. 'en-GB'
 *
 * The tenant whitelist (accounts.supported_locales_json) — when present —
 * narrows the set of locales offered by the picker and constrains
 * resolveLocale's fallback choices.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { customerApi } from './customer_api';
import { LocaleProvider, resolveLocale } from './i18n/i18n';
import { type Locale, SUPPORTED_LOCALES, isSupportedLocale } from './i18n/locales';
import { CustomerBrandProvider } from './lib/CustomerBrandProvider';

interface ProfileLocaleInfo {
  locale: string | null;
  accountLocale: string | null;
  supportedLocales: string[] | null;
}

function getNavigatorLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;
  return navigator.language ?? null;
}

export function CustomerLocaleProvider({ children }: { children: ReactNode }) {
  // Synchronous initial value — based purely on browser language so the
  // first paint isn't blocked on the profile fetch.
  const initialLocale = resolveLocale({
    navigatorLanguage: getNavigatorLanguage(),
  });
  const [locale, setLocaleInternal] = useState<Locale>(initialLocale);
  const [supported, setSupported] = useState<readonly Locale[]>(SUPPORTED_LOCALES);

  const [profileLoaded, setProfileLoaded] = useState(false);

  // Best-effort upgrade once the profile resolves. Errors (401 unauthenticated,
  // 500 server) silently leave us on the synchronous default.
  useEffect(() => {
    let cancelled = false;
    customerApi<ProfileLocaleInfo>('/api/customer/profile')
      .then((data) => {
        if (cancelled) return;
        const resolved = resolveLocale({
          customerLocale: data.locale,
          accountLocale: data.accountLocale,
          navigatorLanguage: getNavigatorLanguage(),
          supportedLocales: data.supportedLocales,
        });
        if (data.supportedLocales && data.supportedLocales.length > 0) {
          const narrowed = data.supportedLocales.filter(isSupportedLocale);
          if (narrowed.length > 0) setSupported(narrowed);
        }
        setLocaleInternal(resolved);
        setProfileLoaded(true);
      })
      .catch(() => {
        // Not signed in or fetch failed — keep the synchronous default.
        if (!cancelled) setProfileLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (next: Locale) => {
    setLocaleInternal(next);
    // Fire-and-forget persistence — UI updates synchronously, server
    // catches up in the background. Failure leaves the column unchanged;
    // the next refresh will revert (acceptable trade-off for an
    // intentionally simple v1 picker).
    void customerApi('/api/customer/profile', {
      method: 'PATCH',
      body: JSON.stringify({ locale: next }),
    }).catch(() => {
      // Ignored — the in-memory selection still wins for this session.
    });
  };

  // We use `key={profileLoaded ? locale : 'pre'}` so the inner LocaleProvider
  // remounts EXACTLY ONCE when the profile fetch resolves and seeds a new
  // initialLocale. After that, the picker's setLocale flow runs through
  // LocaleProvider's own useState (no remount), and our onLocaleChange
  // callback fires the PATCH.
  const providerKey = profileLoaded ? `loaded-${locale}` : 'pre';
  return (
    <LocaleProvider
      key={providerKey}
      initialLocale={locale}
      supportedLocales={supported}
      onLocaleChange={handleChange}
    >
      {/* One branding fetch for the whole portal: tenant name + colour for the
          persistent header, and the migration-0097 "space" noun for every
          customer page, including those outside a BrandedShell. */}
      <CustomerBrandProvider>{children}</CustomerBrandProvider>
    </LocaleProvider>
  );
}
